import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ShieldCheck, FileText, ArrowLeft, Gavel, Scales, Database,
  MapPin, Users, Warning, Eye, Clock, Fingerprint, Cookie,
  Globe, Handshake, ListChecks, ArrowRight, CaretDown, CaretUp
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";

function CollapsibleSection({ title, icon: Icon, children, defaultOpen = false, id }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden" data-testid={`section-${id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 bg-white hover:bg-zinc-50 transition-colors text-left"
        data-testid={`toggle-${id}`}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon size={20} className="text-[#002FA7] shrink-0" />}
          <h2 className="font-semibold text-base text-zinc-900">{title}</h2>
        </div>
        {open ? <CaretUp size={18} className="text-zinc-400" /> : <CaretDown size={18} className="text-zinc-400" />}
      </button>
      {open && <div className="px-5 pb-5 bg-white">{children}</div>}
    </div>
  );
}

export default function TermsPage() {
  const [policy, setPolicy] = useState(null);
  const [activeTab, setActiveTab] = useState("tos");
  const [pageLang, setPageLang] = useState(() => localStorage.getItem("knoria-lang") || "fr");

  useEffect(() => {
    axios.get(`${API}/api/privacy/policy?lang=${pageLang}`).then(r => setPolicy(r.data)).catch(() => {});
  }, [pageLang]);

  if (!policy) return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="animate-spin h-8 w-8 border-2 border-[#002FA7] border-t-transparent rounded-full" />
    </div>
  );

  const tosSections = policy.terms_of_service?.sections || [];
  const ppSections = policy.privacy_policy?.sections || [];
  const dpaSections = policy.dpa?.sections || [];

  const tabs = [
    { id: "tos", label: "Conditions d'utilisation", icon: Gavel, count: tosSections.length },
    { id: "privacy", label: "Politique de confidentialite", icon: ShieldCheck, count: ppSections.length },
    { id: "dpa", label: "Entente de traitement (DPA)", icon: Handshake, count: dpaSections.length },
    { id: "data", label: "Données et sous-traitants", icon: Database, count: null },
    { id: "rights", label: "Vos droits", icon: Users, count: null },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#002FA7] hover:underline mb-6" data-testid="back-link">
          <ArrowLeft size={14} /> Retour a KNORIA
        </Link>

        <div className="bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8 mb-6" data-testid="terms-header">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#002FA7]/10 flex items-center justify-center shrink-0">
              <Scales size={28} className="text-[#002FA7]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] text-zinc-900">
                Cadre juridique KNORIA
              </h1>
              <p className="text-sm text-zinc-500 mt-1">
                Version {policy.version} — En vigueur depuis le {policy.effective_date} — Derniere mise à jour : {policy.last_updated}
              </p>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <Warning size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">
              Ce document constitue un accord juridiquement contraignant. En utilisant KNORIA, vous acceptez l'integralite des conditions ci-dessous.
              Conforme a la <strong>Loi 25 du Québec</strong>, a la <strong>LPRPSP</strong> et a <strong>PIPEDA</strong>.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="legal-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  active
                    ? "bg-[#002FA7] text-white shadow-md"
                    : "bg-white border border-zinc-200 text-zinc-600 hover:border-[#002FA7]/30 hover:text-[#002FA7]"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(" ")[0]}</span>
                {tab.count && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-zinc-100"}`}>{tab.count}</span>}
              </button>
            );
          })}
        </div>

        {/* Conditions d'utilisation */}
        {activeTab === "tos" && (
          <div className="space-y-3" data-testid="tos-content">
            <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-4">
              <div className="flex items-center gap-3 mb-2">
                <Gavel size={24} className="text-[#002FA7]" />
                <h2 className="text-xl font-bold font-['Outfit']">Conditions d'utilisation</h2>
              </div>
              <p className="text-sm text-zinc-500">{policy.company_full} — {policy.jurisdiction}</p>
            </div>
            {tosSections.map((section, i) => (
              <CollapsibleSection
                key={section.id}
                id={section.id}
                title={section.title}
                icon={FileText}
                defaultOpen={i < 2}
              >
                {/* Definitions table */}
                {section.definitions && (
                  <div className="border border-zinc-200 rounded-lg overflow-hidden mb-4">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50">
                        <tr>
                          <th className="p-3 text-left font-semibold text-zinc-700 w-1/4">Terme</th>
                          <th className="p-3 text-left font-semibold text-zinc-700">Définition</th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.definitions.map((def, j) => (
                          <tr key={j} className="border-t border-zinc-100">
                            <td className="p-3 font-medium text-[#002FA7] align-top">{def.term}</td>
                            <td className="p-3 text-zinc-600">{def.def}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Restrictions list */}
                {section.list && (
                  <ul className="space-y-2 mb-4">
                    {section.list.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-zinc-700">
                        <span className="text-red-500 font-bold mt-0.5 shrink-0">x</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Content paragraphs */}
                {section.content && (
                  <div className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                )}
              </CollapsibleSection>
            ))}
          </div>
        )}

        {/* Politique de confidentialite */}
        {activeTab === "privacy" && (
          <div className="space-y-3" data-testid="privacy-content">
            <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-4">
              <div className="flex items-center gap-3 mb-2">
                <ShieldCheck size={24} className="text-teal-600" />
                <h2 className="text-xl font-bold font-['Outfit']">Politique de confidentialite</h2>
              </div>
              <p className="text-sm text-zinc-500">
                Conforme a la Loi 25 du Québec — {policy.applicable_laws?.[0]}
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {policy.applicable_laws?.map((law, i) => (
                  <span key={i} className="text-[10px] px-2 py-1 bg-teal-50 text-teal-700 rounded-full font-medium border border-teal-200">{law.split(" — ")[0]}</span>
                ))}
              </div>
            </div>
            {ppSections.map((section, i) => {
              const icons = {
                pp_officer: ShieldCheck, pp_consent: ListChecks, pp_data_collected: Database,
                pp_ai_processing: Eye, pp_biometric: Fingerprint, pp_cookies: Cookie,
                pp_cross_border: Globe, pp_retention: Clock, pp_security: ShieldCheck,
                pp_incidents: Warning,
              };
              const Icon = icons[section.id] || FileText;
              return (
                <CollapsibleSection
                  key={section.id}
                  id={section.id}
                  title={section.title}
                  icon={Icon}
                  defaultOpen={i < 2}
                >
                  <div className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </CollapsibleSection>
              );
            })}
          </div>
        )}

        {/* DPA */}
        {activeTab === "dpa" && (
          <div className="space-y-3" data-testid="dpa-content">
            <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-4">
              <div className="flex items-center gap-3 mb-2">
                <Handshake size={24} className="text-[#002FA7]" />
                <h2 className="text-xl font-bold font-['Outfit']">{policy.dpa?.title}</h2>
              </div>
              <p className="text-sm text-zinc-500">Entente regissant le traitement des renseignements personnels par KNORIA Inc.</p>
            </div>
            {dpaSections.map((section, i) => (
              <CollapsibleSection
                key={section.id}
                id={section.id}
                title={section.title}
                icon={FileText}
                defaultOpen={i < 2}
              >
                <div className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </CollapsibleSection>
            ))}
          </div>
        )}

        {/* Données et sous-traitants */}
        {activeTab === "data" && (
          <div className="space-y-4" data-testid="data-content">
            {/* Data collected table */}
            <div className="bg-white border border-zinc-200 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Database size={24} className="text-[#002FA7]" />
                <h2 className="text-xl font-bold font-['Outfit']">Renseignements personnels collectes</h2>
              </div>
              <div className="border border-zinc-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs sm:text-sm" data-testid="data-table">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="p-3 text-left font-semibold">Type</th>
                      <th className="p-3 text-left font-semibold">Exemples</th>
                      <th className="p-3 text-left font-semibold">Finalite</th>
                      <th className="p-3 text-left font-semibold">Conservation</th>
                      <th className="p-3 text-left font-semibold">Base legale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {policy.data_collected?.map((d, i) => (
                      <tr key={i} className="border-t border-zinc-100">
                        <td className="p-3 font-medium text-zinc-900">{d.type}</td>
                        <td className="p-3 text-zinc-500">{d.examples}</td>
                        <td className="p-3 text-zinc-600">{d.purpose}</td>
                        <td className="p-3 text-zinc-500">{d.retention}</td>
                        <td className="p-3">
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded font-medium">{d.legal_basis}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Data Storage */}
            <div className="bg-white border border-zinc-200 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck size={24} className="text-teal-600" />
                <h2 className="text-lg font-bold font-['Outfit']">Stockage et securite</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(policy.data_storage || {}).map(([key, val]) => (
                  <div key={key} className="bg-zinc-50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground capitalize mb-1">{key.replace(/_/g, " ")}</p>
                    <p className="text-sm font-medium text-zinc-800">{val}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Third-party processors */}
            <div className="bg-white border border-zinc-200 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin size={24} className="text-[#002FA7]" />
                <h2 className="text-lg font-bold font-['Outfit']">Sous-traitants et localisation</h2>
              </div>
              <div className="space-y-3" data-testid="processors-list">
                {policy.third_party_processors?.map((tp, i) => (
                  <div key={i} className="border border-zinc-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm text-zinc-900">{tp.name}</span>
                      <span className="text-xs bg-zinc-100 px-2 py-0.5 rounded font-medium">{tp.location}</span>
                    </div>
                    <p className="text-sm text-zinc-600 mb-2">{tp.purpose}</p>
                    <div className="flex flex-wrap gap-1">
                      {tp.safeguards?.split(", ").map((s, j) => (
                        <span key={j} className="text-[10px] px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded font-medium">{s}</span>
                      ))}
                    </div>
                    {tp.efvp && (
                      <p className="text-[10px] text-[#002FA7] mt-2 font-medium">EFVP : {tp.efvp}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Vos droits */}
        {activeTab === "rights" && (
          <div className="space-y-4" data-testid="rights-content">
            <div className="bg-white border border-zinc-200 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Users size={24} className="text-[#002FA7]" />
                <h2 className="text-xl font-bold font-['Outfit']">Vos droits — Loi 25 / LPRPSP</h2>
              </div>
              <div className="space-y-3" data-testid="rights-list">
                {policy.user_rights?.map((r, i) => (
                  <div key={i} className="border border-zinc-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-sm text-zinc-900">Droit a l'{r.right.toLowerCase()}</h3>
                      {r.law_ref && <span className="text-[10px] px-2 py-0.5 bg-[#002FA7]/10 text-[#002FA7] rounded font-mono">{r.law_ref}</span>}
                    </div>
                    <p className="text-sm text-zinc-600">{r.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white border border-zinc-200 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck size={24} className="text-teal-600" />
                <h2 className="text-lg font-bold font-['Outfit']">Exercer vos droits</h2>
              </div>
              <div className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line">
                {policy.contact}
              </div>
            </div>

            {/* Incident procedure */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Warning size={24} className="text-amber-600" />
                <h2 className="text-lg font-bold font-['Outfit'] text-amber-900">Incidents de confidentialite</h2>
              </div>
              <p className="text-sm text-amber-800 leading-relaxed">{policy.incident_procedure}</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-zinc-400 py-4 border-t border-zinc-200" data-testid="terms-footer">
          <p className="mb-1">KNORIA Inc. — Knowledge Empowered | {policy.jurisdiction}</p>
          <p>Version {policy.version} | {policy.effective_date} | &copy; 2026 Tous droits reserves</p>
          <p className="mt-2 text-zinc-500">
            Commission d'acces a l'information du Québec (CAI) — <a href="https://www.cai.gouv.qc.ca/" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">cai.gouv.qc.ca</a>
          </p>
        </div>
      </div>
    </div>
  );
}
